import React from 'react'

import ratingOnly from 'assets/svg/rating-filter/RatingOnly.svg'
import reviewOnly from 'assets/svg/rating-filter/ReviewOnly.svg'
import ratingsAndReviews from 'assets/svg/rating-filter/ReviewsAndRatings.svg'
import {
  PaneRadioInput,
  PaneTypeCard,
  PaneTypeCardBody,
  PaneTypeCardDescription,
  PaneTypeCardHeader,
  PaneTypeCardLabel,
  RatingTypeCardPreviewImage,
  RatingTypeCardPreviewRow,
} from 'features/charts-v2/components/filter-popover-pane.styles'

type ImageKey = 'ratingsAndReviews' | 'reviewOnly' | 'ratingOnly'

const ALL_IMAGES: Array<{ key: ImageKey; src: string; alt: string }> = [
  {
    key: 'ratingsAndReviews',
    src: ratingsAndReviews,
    alt: 'card displaying ratings and reviews',
  },
  {
    key: 'reviewOnly',
    src: reviewOnly,
    alt: 'card displaying reviews only',
  },
  {
    key: 'ratingOnly',
    src: ratingOnly,
    alt: 'card displaying ratings only',
  },
]

const RATING_OPTIONS: Array<{
  value: number
  label: string
  description: string
  activeImageKeys: ImageKey[]
}> = [
  {
    value: 0,
    label: 'Reviews & Ratings',
    description: 'Both text reviews and numeric star ratings.',
    activeImageKeys: ['ratingsAndReviews', 'reviewOnly', 'ratingOnly'],
  },
  {
    value: 1,
    label: 'Reviews Only',
    description: 'Only reviews with text.',
    activeImageKeys: ['ratingsAndReviews', 'reviewOnly'],
  },
  {
    value: 2,
    label: 'Ratings Only',
    description: 'Only numeric star ratings, without text.',
    activeImageKeys: ['ratingOnly'],
  },
]

interface RatingTypeSelectorProps {
  value: number
  onChange: (value: number) => void
  // Restricts which types may be NEWLY selected — e.g. a dashboard global
  // Rating filter only allows a subset (see `deriveFilterRestriction`).
  // Undefined = unrestricted. The currently-selected value stays visible and
  // selectable even when disallowed, so the user can see and change it —
  // only OTHER disallowed options are inert, mirroring how a now-disallowed
  // value stays checked-but-visible in `restrictFilterOptions`.
  allowedValues?: readonly number[]
}

// The three "Type" radio cards (Reviews & Ratings / Reviews Only / Ratings
// Only), each with its description and preview-card row. Pure value/onChange
// — the parent pane owns what selecting a type implies for the null-rows
// draft.
export const RatingTypeSelector: React.FC<RatingTypeSelectorProps> = ({
  value,
  onChange,
  allowedValues,
}) => (
  <div>
    {RATING_OPTIONS.map(
      ({ value: optionValue, label, description, activeImageKeys }) => {
        const isSelected = value === optionValue
        const isDisabled =
          !isSelected &&
          allowedValues !== undefined &&
          !allowedValues.includes(optionValue)
        return (
          <PaneTypeCard
            key={optionValue}
            $selected={isSelected}
            $disabled={isDisabled}
            onClick={() => !isDisabled && onChange(optionValue)}
          >
            <PaneTypeCardHeader>
              <PaneRadioInput
                type="radio"
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => !isDisabled && onChange(optionValue)}
              />
              <PaneTypeCardLabel $selected={isSelected}>
                {label}
              </PaneTypeCardLabel>
            </PaneTypeCardHeader>
            <PaneTypeCardBody>
              <PaneTypeCardDescription>{description}</PaneTypeCardDescription>
              <RatingTypeCardPreviewRow>
                {ALL_IMAGES.map((img) => (
                  <RatingTypeCardPreviewImage
                    key={img.alt}
                    alt={img.alt}
                    src={img.src}
                    $active={activeImageKeys.includes(img.key)}
                  />
                ))}
              </RatingTypeCardPreviewRow>
            </PaneTypeCardBody>
          </PaneTypeCard>
        )
      },
    )}
  </div>
)
